import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { FileText, Video, X, Settings2, Trash2, Upload } from 'lucide-react';
import { api } from '../lib/api';
import { SOPS, SopVideoModal } from './SopVideoButton';

const AUD = [['monteur', 'Monteurs'], ['posteur', 'Posteurs'], ['all', 'Tout le monde']];
const CHUNK = 4 * 1024 * 1024;
const noSave = { onContextMenu: (e) => e.preventDefault(), onDragStart: (e) => e.preventDefault() };

const pill = (accent) => ({
  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px 7px 9px', borderRadius: 999, cursor: 'pointer',
  background: `rgba(${accent},0.14)`, border: `1px solid rgba(${accent},0.45)`, color: `rgb(${accent})`,
  fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
});

const Overlay = ({ children, onClose, testid }) => createPortal(
  <div data-testid={testid} onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 220, background: 'rgba(6,12,26,0.82)', backdropFilter: 'blur(14px)', display: 'grid', placeItems: 'center', padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 920, maxHeight: '92vh', overflowY: 'auto', borderRadius: 22, padding: 18, background: 'linear-gradient(180deg, rgba(20,29,66,0.92) 0%, rgba(14,22,44,0.98) 100%)', border: '1px solid rgba(245,197,106,0.28)' }}>
      {children}
    </div>
  </div>,
  document.body,
);

const Header = ({ title, onClose }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
    <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: '#F1F4F9' }}>{title}</div>
    <button type="button" data-testid="sop-modal-close" onClick={onClose} style={{ padding: 8, borderRadius: 10, background: 'transparent', border: '1px solid rgba(62,123,250,0.18)', color: 'rgba(200,215,240,0.72)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={15} /></button>
  </div>
);

const useBlob = (url) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u;
    api.get(url, { responseType: 'blob' }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => setSrc('error'));
    return () => u && URL.revokeObjectURL(u);
  }, [url]);
  return src;
};

const Page = ({ sopId, n }) => {
  const src = useBlob(`/sops/${sopId}/page/${n}`);
  if (!src) return <div style={{ height: 400, borderRadius: 10, background: 'rgba(255,255,255,0.04)' }} />;
  return <img src={src} alt="" draggable={false} {...noSave} style={{ width: '100%', borderRadius: 10, display: 'block', userSelect: 'none', pointerEvents: 'none' }} />;
};

const SopVideo = ({ sopId }) => {
  const src = useBlob(`/sops/${sopId}/media`);
  if (!src) return <div style={{ padding: 40, textAlign: 'center', color: 'rgba(200,215,240,0.6)', fontFamily: 'Sora' }}>Chargement de la vidéo…</div>;
  return <video data-testid="managed-sop-video" src={src} controls autoPlay playsInline controlsList="nodownload noplaybackrate" disablePictureInPicture {...noSave} style={{ width: '100%', maxHeight: '62vh', borderRadius: 14, background: '#000' }} />;
};

const SopViewer = ({ sop, onClose }) => (
  <Overlay onClose={onClose} testid="managed-sop-viewer">
    <Header title={sop.title} onClose={onClose} />
    {sop.kind === 'pdf'
      ? <div {...noSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{Array.from({ length: sop.pages }, (_, i) => <Page key={i} sopId={sop.id} n={i} />)}</div>
      : <SopVideo sopId={sop.id} />}
  </Overlay>
);

const AudiencePicker = ({ value, onChange, testid }) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {AUD.map(([k, l]) => {
      const on = value.includes(k);
      return <button key={k} type="button" data-testid={`${testid}-${k}`} onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])} style={{ padding: '5px 11px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, background: on ? 'rgba(245,197,106,0.18)' : 'transparent', border: `1px solid ${on ? 'rgba(245,197,106,0.6)' : 'rgba(62,123,250,0.25)'}`, color: on ? '#F5C56A' : 'rgba(200,215,240,0.7)' }}>{l}</button>;
    })}
  </div>
);

const SopManager = ({ sops, onClose, reload }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [aud, setAud] = useState(['monteur', 'posteur']);
  const [progress, setProgress] = useState(null);
  const upload = async () => {
    const ct = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'video/mp4');
    try {
      setProgress(0);
      const { data } = await api.post('/sops/init', { title: title || file.name.replace(/\.[^.]+$/, ''), audience: aud, filename: file.name, content_type: ct });
      for (let off = 0; off < file.size; off += CHUNK) {
        await api.put(`/sops/${data.id}/chunk`, file.slice(off, off + CHUNK), { headers: { 'Content-Type': 'application/octet-stream' } });
        setProgress(Math.round(((off + CHUNK) / file.size) * 90));
      }
      await api.post(`/sops/${data.id}/complete`, {}, { timeout: 300000 });
      toast.success('SOP ajouté ✓');
      setFile(null); setTitle(''); setProgress(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur pendant l\'envoi'); setProgress(null); }
  };
  const patch = async (s, body) => { try { await api.patch(`/sops/${s.id}`, body); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } };
  const del = async (s) => { if (!window.confirm(`Supprimer « ${s.title} » ?`)) return; await api.delete(`/sops/${s.id}`); toast.success('SOP supprimé'); reload(); };
  const field = { width: '100%', padding: '9px 11px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(62,123,250,0.25)', color: '#F1F4F9', fontFamily: 'Lexend', fontSize: 13 };
  return (
    <Overlay onClose={onClose} testid="sop-manager">
      <Header title="Gérer les SOP" onClose={onClose} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
        {sops.length === 0 && <div style={{ fontFamily: 'Sora', fontSize: 12.5, color: 'rgba(200,215,240,0.55)' }}>Aucun SOP pour l'instant.</div>}
        {sops.map((s) => (
          <div key={s.id} data-testid={`sop-row-${s.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(62,123,250,0.18)' }}>
            {s.kind === 'pdf' ? <FileText size={15} color="#5BBEFC" /> : <Video size={15} color="#5BBEFC" />}
            <input defaultValue={s.title} onBlur={(e) => e.target.value !== s.title && patch(s, { title: e.target.value })} data-testid={`sop-title-${s.id}`} style={{ ...field, flex: 1, minWidth: 160, padding: '6px 9px' }} />
            <AudiencePicker value={s.audience} onChange={(a) => a.length && patch(s, { audience: a })} testid={`sop-aud-${s.id}`} />
            <button type="button" data-testid={`sop-delete-${s.id}`} onClick={() => del(s)} style={{ padding: 7, borderRadius: 9, background: 'transparent', border: '1px solid rgba(248,113,113,0.4)', color: '#F87171', cursor: 'pointer' }}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <div style={{ padding: 14, borderRadius: 14, border: '1px dashed rgba(245,197,106,0.45)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: '#F5C56A' }}>Ajouter un SOP (vidéo ou PDF)</div>
        <input type="file" accept="video/*,application/pdf" data-testid="sop-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ color: 'rgba(200,215,240,0.8)', fontFamily: 'Sora', fontSize: 12 }} />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nom du SOP (ex : SOP Miniatures)" data-testid="sop-new-title" style={field} />
        <AudiencePicker value={aud} onChange={setAud} testid="sop-new-aud" />
        <button type="button" data-testid="sop-upload-btn" disabled={!file || !aud.length || progress !== null} onClick={upload} className="asa-btn asa-btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Upload size={14} />{progress !== null ? `Envoi… ${Math.min(progress, 95)}%` : 'Envoyer'}
        </button>
        <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'rgba(200,215,240,0.5)' }}>Lisible uniquement dans ASA, sans bouton de téléchargement.</div>
      </div>
    </Overlay>
  );
};

const SopMenu = ({ items, isAdmin, onPick, onManage, onClose }) => (
  <Overlay onClose={onClose} testid="sop-menu">
    <Header title="SOP" onClose={onClose} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.length === 0 && <div style={{ fontFamily: 'Sora', fontSize: 12.5, color: 'rgba(200,215,240,0.55)' }}>Aucun SOP pour toi pour l'instant.</div>}
      {items.map((s) => (
        <button key={s.id} type="button" data-testid={`sop-menu-item-${s.id}`} onClick={() => onPick(s)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', borderRadius: 14, cursor: 'pointer', textAlign: 'left', background: 'rgba(245,197,106,0.06)', border: '1px solid rgba(245,197,106,0.28)', color: '#F1F4F9', fontFamily: 'Sora', fontWeight: 700, fontSize: 14 }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #F5C56A, #D99A42)', color: '#0B1330', flexShrink: 0 }}>{s.kind === 'pdf' ? <FileText size={16} /> : <Video size={16} />}</span>
          <span style={{ flex: 1 }}>{s.title}</span>
          <span style={{ fontSize: 11, color: 'rgba(200,215,240,0.5)' }}>{s.kind === 'pdf' ? 'Document' : 'Vidéo'}</span>
        </button>
      ))}
      {isAdmin && <button type="button" data-testid="sop-manage-btn" onClick={onManage} style={{ ...pill('91,190,252'), alignSelf: 'flex-start', marginTop: 6 }}><Settings2 size={14} />Gérer les SOP</button>}
    </div>
  </Overlay>
);

// Un seul bouton « SOP » → liste des SOP visibles (intégrés + ajoutés depuis ASA).
export function ManagedSopButtons({ isAdmin, isMonteur, isPosteur, compact, iconOnly }) {
  const [sops, setSops] = useState([]);
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(null);
  const [managing, setManaging] = useState(false);
  const reload = () => api.get('/sops').then((r) => setSops(r.data || [])).catch(() => {});
  useEffect(() => { reload(); }, []);
  const builtins = [
    (isMonteur || isAdmin) && { id: 'builtin-montage', builtin: 'montage', title: SOPS.montage.title, kind: 'video' },
    (isPosteur || isAdmin) && { id: 'builtin-posteur', builtin: 'posteur', title: SOPS.posteur.title, kind: 'video' },
  ].filter(Boolean);
  const visible = [...builtins, ...sops.filter((s) => isAdmin || s.audience.includes('all') || (isMonteur && s.audience.includes('monteur')) || (isPosteur && s.audience.includes('posteur')))];
  if (!visible.length && !isAdmin) return null;
  return (
    <>
      {iconOnly ? (
        <button type="button" data-testid="sop-btn" title="SOP" onClick={() => setMenu(true)} style={{ width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'rgba(245,197,106,0.10)', border: '1px solid rgba(245,197,106,0.35)', color: '#F5C56A', cursor: 'pointer', transition: 'background-color 0.14s ease' }}>
          <FileText size={16} strokeWidth={2.4} />
        </button>
      ) : (
        <button type="button" data-testid="sop-btn" onClick={() => setMenu(true)} style={{ ...pill('245,197,106'), height: compact ? 36 : 'auto' }}><FileText size={14} />SOP{visible.length > 1 ? ` · ${visible.length}` : ''}</button>
      )}
      {menu && <SopMenu items={visible} isAdmin={isAdmin} onClose={() => setMenu(false)} onPick={(s) => { setMenu(false); setOpen(s); }} onManage={() => { setMenu(false); setManaging(true); }} />}
      {open && (open.builtin
        ? createPortal(<SopVideoModal sop={SOPS[open.builtin]} variant={open.builtin} onClose={() => setOpen(null)} />, document.body)
        : <SopViewer sop={open} onClose={() => setOpen(null)} />)}
      {managing && <SopManager sops={sops} onClose={() => setManaging(false)} reload={reload} />}
    </>
  );
}
